import { Inviter, UserAgent, Registerer, SessionState } from 'sip.js';

// eslint-disable-next-line import/no-extraneous-dependencies
import { store, getSessionById } from 'src/store/index';
import { setDialNumber, setShowKeyboard } from 'src/store/slices/dialer-slice';
import {
  addSession,
  endConference,
  removeSession,
  setRegisterUA,
  updateSession,
  addIncomingCall,
  createConference,
  addMonitorSession,
  removeIncomingCall,
  removeMonitorSession,
} from 'src/store/slices/call-slice';

import { USER_KEY } from './constant';
import { showToast } from './toaster';
import SafeStorage from './safe-storage';
import { endpoints, newAxiosInstance } from './axios';
import {
  playTone,
  stopTone,
  CALL_TYPE,
  CALL_STATUS,
  normalizeSession,
  handleCallRejectionTone,
} from './sip';
import {
  canMergeSessions,
  logConferenceEvent,
  generateConferenceId,
  muteHostInConference,
  restoreOriginalAudio,
  getActiveConferenceId,
  getConferenceSessions,
  isSessionInConference,
  unmuteHostInConference,
  createConferenceAudioMix,
  replaceSessionAudioTrack,
  muteConferenceParticipant,
  addParticipantsToConference,
  unmuteConferenceParticipant,
} from './conference';

/* ---------------------------------------------------
   Internal Variables
--------------------------------------------------- */
let userAgentInstance = null;
let registerer = null;

// Without an ICE server, the browser's PeerConnection can't gather usable
// candidates on networks without direct host connectivity (e.g. behind NAT),
// which yields an SDP offer with zero a=candidate lines. FreeSWITCH then logs
// "no suitable candidates found" and immediately hangs up with
// INCOMPATIBLE_DESTINATION (488), regardless of what number was dialed.
const ICE_SERVERS = [{ urls: 'stun:stun.l.google.com:19302' }];
let configFromBackend = {};
let ringtoneAudio = null;
let reconnectAttempts = 0;
let reconnectTimer = null;
let healthCheckTimer = null;
let isTabVisible = true;
let lastVisibilityChange = Date.now();

// Reconnection configuration
const RECONNECT_CONFIG = {
  maxAttempts: 10,
  baseDelay: 1000, // 1 second
  maxDelay: 30000, // 30 seconds
  healthCheckInterval: 30000, // 30 seconds
};

// Debug logger with timestamp
function debugLog(category, message, data = null) {
  const timestamp = new Date().toISOString();
  const logPrefix = `[SIP-${category}] ${timestamp}:`;

  if (data) {
    console.log(logPrefix, message, data);
  } else {
    console.log(logPrefix, message);
  }
}

/* ---------------------------------------------------
   Utilities
--------------------------------------------------- */

/**
 * Lookup caller name using CNAM API
 * @param {string} phoneNumber - Phone number to lookup
 * @returns {Promise<{name: string, number: string, uuid: string} | null>}
 */
async function lookupCNAM(phoneNumber) {
  try {
    if (!phoneNumber || !configFromBackend?.sip_domain) {
      return null;
    }

    const domain = configFromBackend.sip_domain;
    const response = await newAxiosInstance.get(endpoints.cnam.lookup(domain, phoneNumber));

    if (response?.data?.data?.name) {
      return response.data.data;
    }
    return null;
  } catch (error) {
    debugLog('CNAM', 'CNAM lookup failed', { phoneNumber, error: error.message });
    return null;
  }
}

function checkUserRegistered() {
  return store.getState().call.isRegistered.status;
}

function requestAudioPermissions() {
  return navigator.mediaDevices.getUserMedia({ audio: true, video: false });
}

/**
 * Parse Reason header field from SIP response
 * Format: Reason: SIP ;cause=200 ;text="Call completed elsewhere"
 * @param {Object} response - SIP response message
 * @returns {string|null} - Reason text or null if not found
 */
function parseReasonHeader(response) {
  try {
    const reasonHeader = response.message?.getHeader('Reason');
    if (!reasonHeader) return null;

    // Match the text parameter: text="..."
    const textMatch = reasonHeader.match(/text="([^"]+)"/);
    if (textMatch && textMatch[1]) {
      return textMatch[1];
    }

    // Fallback: try to extract cause value if text is not present
    const causeMatch = reasonHeader.match(/cause=(\d+)/);
    if (causeMatch && causeMatch[1]) {
      return `Error code: ${causeMatch[1]}`;
    }

    return null;
  } catch (error) {
    console.error('Error parsing Reason header:', error);
    return null;
  }
}

export function playRingtone() {
  const state = store.getState();
  const activeSessions = state.call.sessions || [];
  const hasActiveCall = activeSessions.some((s) => s.state === CALL_STATUS.ESTABLISHED);

  const ringtoneFile = hasActiveCall ? '/media/call-waiting.mp3' : '/media/ringtone.mp3';

  ringtoneAudio = new Audio(ringtoneFile);
  ringtoneAudio.loop = true;
  if (hasActiveCall) {
    ringtoneAudio.volume = 0.05;
  }
  ringtoneAudio.play().catch(console.error);
}

export function stopRingtone() {
  if (ringtoneAudio) {
    ringtoneAudio.pause();
    ringtoneAudio.currentTime = 0;
    ringtoneAudio = null;
  }
}

/* ---------------------------------------------------
   WebSocket Connection Monitoring & Auto-Reconnect
--------------------------------------------------- */

// Check if WebSocket transport is connected
function isTransportConnected() {
  if (!userAgentInstance?.transport) {
    debugLog('HEALTH', 'No transport available');
    return false;
  }

  const { state } = userAgentInstance.transport;
  const isConnected = state === 'Connected';

  debugLog('HEALTH', `Transport state: ${state}, Connected: ${isConnected}`);
  return isConnected;
}

// Calculate exponential backoff delay
function getReconnectDelay() {
  const delay = Math.min(
    RECONNECT_CONFIG.baseDelay * 2 ** reconnectAttempts,
    RECONNECT_CONFIG.maxDelay
  );
  debugLog(
    'RECONNECT',
    `Calculated delay: ${delay}ms (attempt ${reconnectAttempts + 1}/${RECONNECT_CONFIG.maxAttempts})`
  );
  return delay;
}

// Clear reconnection timer
function clearReconnectTimer() {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
    debugLog('RECONNECT', 'Reconnect timer cleared');
  }
}

// Clear health check timer
function clearHealthCheckTimer() {
  if (healthCheckTimer) {
    clearInterval(healthCheckTimer);
    healthCheckTimer = null;
    debugLog('HEALTH', 'Health check timer cleared');
  }
}

// Attempt to reconnect with exponential backoff
async function attemptReconnect(reason = 'Unknown') {
  debugLog('RECONNECT', `Reconnection triggered. Reason: ${reason}`);

  // Don't reconnect if user manually unregistered
  if (!checkUserRegistered()) {
    debugLog('RECONNECT', 'User is not registered, skipping reconnect');
    return;
  }

  // Check if we've exceeded max attempts
  if (reconnectAttempts >= RECONNECT_CONFIG.maxAttempts) {
    debugLog(
      'RECONNECT',
      `Max reconnect attempts (${RECONNECT_CONFIG.maxAttempts}) reached`,
      'error'
    );
    store.dispatch(setRegisterUA({ status: false, error: 'Max reconnection attempts reached' }));
    showToast('Unable to reconnect to SIP server. Please refresh the page.', 'error');
    return;
  }

  clearReconnectTimer();
  reconnectAttempts += 1;

  const delay = getReconnectDelay();
  debugLog('RECONNECT', `Scheduling reconnect attempt ${reconnectAttempts} in ${delay}ms`);

  reconnectTimer = setTimeout(async () => {
    try {
      debugLog('RECONNECT', `Starting reconnect attempt ${reconnectAttempts}...`);

      // Stop existing UserAgent gracefully
      if (userAgentInstance) {
        try {
          await userAgentInstance.stop();
          debugLog('RECONNECT', 'Stopped existing UserAgent');
        } catch (err) {
          debugLog('RECONNECT', 'Error stopping UserAgent', err);
        }
      }

      // Restart with existing config
      if (configFromBackend?.phone_default_enabled) {
        debugLog('RECONNECT', 'Restarting UserAgent with config', {
          sip_user: configFromBackend.sip_user,
          sip_domain: configFromBackend.sip_domain,
        });

        await start(configFromBackend);

        // Reset reconnect counter on success
        reconnectAttempts = 0;
        debugLog('RECONNECT', 'Reconnection successful! Reset attempt counter.');
        showToast('Reconnected to SIP server', 'success');
      }
    } catch (error) {
      debugLog('RECONNECT', `Reconnect attempt ${reconnectAttempts} failed`, error);
      // Try again
      attemptReconnect(`Retry after failure: ${error.message}`);
    }
  }, delay);
}

// Health check to monitor connection
function startHealthCheck() {
  clearHealthCheckTimer();

  debugLog('HEALTH', `Starting health check (interval: ${RECONNECT_CONFIG.healthCheckInterval}ms)`);

  healthCheckTimer = setInterval(() => {
    debugLog('HEALTH', 'Running health check...');

    const isRegistered = checkUserRegistered();
    const isConnected = isTransportConnected();

    debugLog('HEALTH', `Status - Registered: ${isRegistered}, Transport Connected: ${isConnected}`);

    // If registered but transport is disconnected, attempt reconnect
    if (isRegistered && !isConnected) {
      debugLog('HEALTH', 'Transport disconnected but user is registered. Triggering reconnect...');
      attemptReconnect('Health check detected disconnection');
    } else if (isRegistered && isConnected) {
      // Connection is healthy, reset reconnect counter
      if (reconnectAttempts > 0) {
        debugLog('HEALTH', 'Connection restored, resetting reconnect attempts');
        reconnectAttempts = 0;
      }
    }
  }, RECONNECT_CONFIG.healthCheckInterval);
}

// Handle page visibility changes
function handleVisibilityChange() {
  const now = Date.now();
  const wasVisible = isTabVisible;
  isTabVisible = document.visibilityState === 'visible';

  debugLog(
    'VISIBILITY',
    `Tab visibility changed: ${wasVisible ? 'visible' : 'hidden'} → ${isTabVisible ? 'visible' : 'hidden'}`
  );
  debugLog('VISIBILITY', `Time since last change: ${now - lastVisibilityChange}ms`);

  if (isTabVisible && !wasVisible) {
    // Tab became visible
    const timeSinceHidden = now - lastVisibilityChange;
    debugLog('VISIBILITY', `Tab became visible after ${timeSinceHidden}ms`);

    // If tab was hidden for more than 1 minute, check connection
    if (timeSinceHidden > 60000) {
      debugLog('VISIBILITY', 'Tab was hidden for >1 minute, checking WebSocket connection...');

      if (checkUserRegistered() && !isTransportConnected()) {
        debugLog('VISIBILITY', 'WebSocket disconnected while tab was hidden. Reconnecting...');
        attemptReconnect('Tab became visible after long period');
      }
    }
  }

  lastVisibilityChange = now;
}

// Handle network online/offline events
function handleOnlineEvent() {
  debugLog('NETWORK', 'Network came ONLINE');

  if (checkUserRegistered()) {
    debugLog('NETWORK', 'User is registered, checking connection...');

    // Give the network a moment to stabilize
    setTimeout(() => {
      if (!isTransportConnected()) {
        debugLog('NETWORK', 'Transport not connected after coming online. Reconnecting...');
        attemptReconnect('Network came online');
      }
    }, 1000);
  }
}

function handleOfflineEvent() {
  debugLog('NETWORK', 'Network went OFFLINE');
  clearReconnectTimer(); // Don't try to reconnect while offline
}

// Handle page focus/blur (sleep/wake detection)
function handlePageFocus() {
  debugLog('FOCUS', 'Page gained FOCUS (potential wake from sleep)');

  if (checkUserRegistered()) {
    // Check connection after a brief delay to let system stabilize
    setTimeout(() => {
      if (!isTransportConnected()) {
        debugLog('FOCUS', 'Transport disconnected after wake. Reconnecting...');
        attemptReconnect('Page gained focus after sleep');
      }
    }, 500);
  }
}

function handlePageBlur() {
  debugLog('FOCUS', 'Page lost FOCUS');
}

// Initialize all event listeners
function initializeConnectionMonitoring() {
  debugLog('INIT', 'Initializing WebSocket connection monitoring...');

  // Page visibility
  document.addEventListener('visibilitychange', handleVisibilityChange);
  debugLog('INIT', '✓ Page visibility listener added');

  // Network connectivity
  window.addEventListener('online', handleOnlineEvent);
  window.addEventListener('offline', handleOfflineEvent);
  debugLog('INIT', '✓ Network online/offline listeners added');

  // Focus/blur (sleep/wake detection)
  window.addEventListener('focus', handlePageFocus);
  window.addEventListener('blur', handlePageBlur);
  debugLog('INIT', '✓ Focus/blur listeners added');

  // Start health check
  startHealthCheck();
  debugLog('INIT', '✓ Health check started');

  debugLog('INIT', 'WebSocket connection monitoring initialized successfully');
}

// Cleanup all event listeners
function cleanupConnectionMonitoring() {
  debugLog('CLEANUP', 'Cleaning up WebSocket connection monitoring...');

  document.removeEventListener('visibilitychange', handleVisibilityChange);
  window.removeEventListener('online', handleOnlineEvent);
  window.removeEventListener('offline', handleOfflineEvent);
  window.removeEventListener('focus', handlePageFocus);
  window.removeEventListener('blur', handlePageBlur);

  clearReconnectTimer();
  clearHealthCheckTimer();

  debugLog('CLEANUP', 'WebSocket connection monitoring cleaned up');
}

/* ---------------------------------------------------
   SIP Setup & Registration
--------------------------------------------------- */
export async function toggleRegistration() {
  const isRegistered = checkUserRegistered();
  const config = SafeStorage.getSessionJSON(USER_KEY);

  try {
    const response = await newAxiosInstance.patch(
      endpoints.users.dynamicPoints(config?.uuid, 'profile/'),
      { phone_default_enabled: !isRegistered }
    );

    // Handle nested response structure - backend returns {data: {data: {phone_default_enabled: true}}}
    const phoneEnabled =
      response.data?.data?.phone_default_enabled ?? response.data?.phone_default_enabled;

    // Dynamic import to avoid circular dependency
    const { updateUserSession } = await import('src/auth/context/jwt');
    updateUserSession({ phone_default_enabled: phoneEnabled });

    if (phoneEnabled) {
      debugLog('REGISTRATION', 'Enabling registration...');
      await start({ ...config, phone_default_enabled: true });
    } else {
      debugLog('REGISTRATION', 'Disabling registration...');

      // CRITICAL FIX: Update Redux state FIRST to prevent auto-reconnect
      store.dispatch(setRegisterUA({ status: false, error: null }));

      // Then cleanup monitoring and stop UserAgent
      cleanupConnectionMonitoring();
      if (userAgentInstance) {
        await userAgentInstance.stop();
      }

      showToast('Unregistered successfully');
    }
  } catch (error) {
    debugLog('REGISTRATION', 'Toggle registration error', error);
    showToast(error.message, 'error');
  }
}

export async function start(config) {
  try {
    debugLog('START', '═══════════════════════════════════════════════════');
    debugLog('START', 'Starting SIP UserAgent...', {
      sip_user: config.sip_user,
      sip_domain: config.sip_domain,
      sip_port: config.sip_port || 7443,
      phone_enabled: config.phone_default_enabled,
    });

    configFromBackend = config;
    const sipUri = `sip:${config.sip_user}@${config.sip_domain}`;
    const wsServer = `wss://${config.sip_domain}:${config.sip_port || 7443}`;

    debugLog('START', `SIP URI: ${sipUri}`);
    debugLog('START', `WebSocket Server: ${wsServer}`);

    const ua = new UserAgent({
      uri: UserAgent.makeURI(sipUri),
      transportOptions: {
        server: wsServer,
        traceSip: true,
      },
      authorizationUsername: config.sip_user,
      authorizationPassword: config.sip_password,
      sessionDescriptionHandlerFactoryOptions: {
        peerConnectionConfiguration: {
          iceServers: ICE_SERVERS,
        },
      },
      delegate: {
        onInvite: (invitation) => {
          debugLog('INVITE', 'Incoming call received', {
            from: invitation.remoteIdentity?.uri?.user,
            id: invitation.id,
          });
          handleIncomingCall(invitation);
        },
        onDisconnect: (error) => {
          debugLog('DISCONNECT', 'UserAgent disconnected', error);
          // Trigger reconnect on disconnect
          if (checkUserRegistered()) {
            attemptReconnect('UserAgent delegate onDisconnect');
          }
        },
      },

      autoStart: false,
      autoStop: true,
      logLevel: 'log',
      logBuiltinEnabled: true,
    });

    // Enhanced transport delegate with detailed logging
    ua.transport.delegate = {
      onConnect: () => {
        debugLog('TRANSPORT', '✓ WebSocket transport CONNECTED');
        debugLog('TRANSPORT', `Connection URL: ${wsServer}`);
        // Reset reconnect attempts on successful connection
        reconnectAttempts = 0;
      },
      onDisconnect: (error) => {
        debugLog('TRANSPORT', '✗ WebSocket transport DISCONNECTED', {
          error: error?.message || 'No error details',
          hasError: !!error,
        });

        // Trigger reconnect if user is still registered
        if (checkUserRegistered()) {
          debugLog('TRANSPORT', 'User still registered, attempting reconnect...');
          attemptReconnect('Transport onDisconnect');
        }
      },
      onMessage: (message) => {
        debugLog('TRANSPORT', `Message received: ${message.substr(0, 100)}...`);
      },
    };

    userAgentInstance = ua;
    debugLog('START', 'UserAgent instance created');

    if (config?.phone_default_enabled) {
      debugLog('START', 'Phone is enabled, starting registration...');

      registerer = new Registerer(ua);
      debugLog('START', 'Registerer created');

      await ua.start();
      debugLog('START', 'UserAgent started');

      await registerer.register();
      debugLog('START', '✓ Registration successful');

      store.dispatch(setRegisterUA({ status: true, error: null }));

      // Initialize connection monitoring after successful registration
      initializeConnectionMonitoring();
      debugLog('START', '✓ Connection monitoring initialized');

      debugLog('START', '═══════════════════════════════════════════════════');
      debugLog('START', '✓✓✓ SIP UserAgent started successfully ✓✓✓');
      debugLog('START', '═══════════════════════════════════════════════════');
    } else {
      debugLog('START', 'Phone not enabled, skipping registration');
    }

    return userAgentInstance;
  } catch (err) {
    debugLog('START', '✗✗✗ Registration FAILED ✗✗✗', {
      error: err.message,
      stack: err.stack,
    });

    store.dispatch(setRegisterUA({ status: false, error: err.message }));
    showToast(`Registration error: ${err.message}`, 'error');

    // Attempt reconnect on startup failure
    if (config?.phone_default_enabled) {
      debugLog('START', 'Scheduling reconnect after startup failure...');
      attemptReconnect(`Startup failure: ${err.message}`);
    }

    return null;
  }
}

/* ---------------------------------------------------
   Handle Incoming Call
--------------------------------------------------- */

export async function handleIncomingCall(invitation) {
  try {
    const sessionId = invitation.id || invitation.request.callId;
    debugLog('INCOMING', '📞 Incoming call detected', {
      sessionId,
      from: invitation.remoteIdentity?.uri?.user,
      callerName: invitation.remoteIdentity?.displayName,
    });

    const normalizedSession = normalizeSession(invitation, CALL_TYPE.INCOMING);
    const audioElemId = `call-audio-${sessionId}`;

    const state = store.getState();
    const existingSession = state.call.sessions.find((s) => s.id === sessionId);
    debugLog('INCOMING', `Existing session found: ${!!existingSession}`);

    if (!existingSession) {
      store.dispatch(addSession({ ...normalizedSession, state: CALL_STATUS.RINGING }));
    } else {
      store.dispatch(updateSession({ id: sessionId, state: CALL_STATUS.RINGING }));
    }

    store.dispatch(addIncomingCall({ ...normalizedSession, state: CALL_STATUS.RINGING }));

    // CNAM lookup to get caller name (async - won't block UI)
    const callerNumber = normalizedSession.callerNumber || invitation.remoteIdentity?.uri?.user;
    if (callerNumber) {
      lookupCNAM(callerNumber).then((cnamData) => {
        if (cnamData?.name) {
          store.dispatch(
            updateSession({
              id: sessionId,
              callerName: cnamData.name,
              contactUuid: cnamData.uuid || null,
            })
          );
          // Also update incoming call modal
          store.dispatch(
            addIncomingCall({
              ...normalizedSession,
              state: CALL_STATUS.RINGING,
              callerName: cnamData.name,
              contactUuid: cnamData.uuid || null,
            })
          );
        }
      });
    }

    // Request audio permission but immediately stop the stream
    // We just need to check/get permission, actual stream will be created on accept
    const permissionStream = await requestAudioPermissions();
    if (permissionStream) {
      permissionStream.getTracks().forEach((track) => track.stop());
    }
    playRingtone();

    invitation.stateChange.addListener((newState) => {
      switch (newState) {
        case SessionState.Established: {
          store.dispatch(updateSession({ id: sessionId, state: CALL_STATUS.ESTABLISHED }));
          store.dispatch(removeIncomingCall(sessionId));
          stopRingtone();

          showToast(`Call from ${normalizedSession.remoteIdentity.uri.user} answered`, 'success');
          break;
        }

        case SessionState.Terminated: {
          const currentState = store
            .getState()
            .call.sessions.find((s) => s.id === sessionId)?.state;

          store.dispatch(updateSession({ id: sessionId, state: CALL_STATUS.TERMINATED }));
          store.dispatch(removeIncomingCall(sessionId));
          store.dispatch(removeSession(sessionId));
          stopRingtone();

          // Extract termination reason if available
          const byeMsg = invitation?.byeRequest?.message;
          const reqMsg = invitation?.request;

          const statusCode = byeMsg?.statusCode || reqMsg?.statusCode || null;
          const reasonPhrase = byeMsg?.reasonPhrase || reqMsg?.reasonPhrase || '';

          // If call wasn't answered, treat as missed/rejected/cancelled
          if (currentState !== CALL_STATUS.ESTABLISHED) {
            const code = statusCode || 486; // 486 = Busy Here
            handleCallRejectionTone(code, audioElemId, false);

            showToast(
              `Call from ${normalizedSession.remoteIdentity.uri.user} was rejected or missed`,
              'warning'
            );
          } else {
            showToast(`Call from ${normalizedSession.remoteIdentity.uri.user} ended`, 'info');
          }

          break;
        }

        default:
        // console.log(`[SIP] Incoming session changed state: ${newState}`);
      }
    });

    invitation.delegate = {
      onCancel: () => {
        store.dispatch(updateSession({ id: sessionId, state: CALL_STATUS.CANCELED }));
        store.dispatch(removeIncomingCall(sessionId));
        store.dispatch(removeSession(sessionId));
        stopRingtone();

        handleCallRejectionTone(487, audioElemId, false); // 487 = Request Terminated
        showToast(
          `Call from ${normalizedSession.remoteIdentity.uri.user} was cancelled by caller`,
          'info'
        );
      },

      onReject: (response) => {
        const code = response?.message?.statusCode || 480;
        // console.log('[SIP] onReject with code:', code);

        stopTone(audioElemId);

        if (code === 486) {
          playTone('busy', audioElemId);
        } else {
          playTone('failed', audioElemId);
        }

        // No need to remove session here; Terminated will handle it
      },
    };
  } catch (error) {
    console.error('Error handling incoming call:', error);
    showToast('Failed to handle incoming call.', 'error');
  }
}

/* ---------------------------------------------------
   Accept Incoming Call
--------------------------------------------------- */
async function holdSession(normalizedSession, hold = true) {
  const session = normalizedSession.rawSession;
  if (!session || !session.sessionDescriptionHandler) {
    console.warn(`No sessionDescriptionHandler found for session ${normalizedSession.id}`);
    return;
  }

  const pc = session.sessionDescriptionHandler.peerConnection;

  // Toggle audio sender tracks enabled/disabled
  pc.getSenders().forEach((sender) => {
    if (sender.track && sender.track.kind === 'audio') {
      sender.track.enabled = !hold; // disable on hold, enable on unhold
      console.log(
        `[Hold] ${hold ? 'Disabling' : 'Enabling'} sender track for session ${normalizedSession.id}`
      );
    }
  });

  const sdpModifier = (description) => {
    description.sdp = description.sdp.replace(
      /a=sendrecv|a=sendonly|a=inactive/g,
      hold ? 'a=sendonly' : 'a=sendrecv'
    );
    return Promise.resolve(description);
  };

  try {
    await session.invite({
      sessionDescriptionHandlerModifiers: [sdpModifier],
      requestDelegate: {
        onAccept: () => {
          console.log(
            `[Hold] Session ${normalizedSession.id} ${hold ? 'put on hold' : 'resumed'} - SDP accepted`
          );
          store.dispatch(
            updateSession({
              id: normalizedSession.rawSession.id,
              isOnHold: hold,
            })
          );

          // CRITICAL FIX: Handle audio element state
          const audioElement = document.getElementById(`call-audio-${normalizedSession.id}`);
          if (audioElement) {
            if (hold) {
              audioElement.pause();
              console.log(`[Hold] Audio element paused for session ${normalizedSession.id}`);
            } else {
              audioElement.play().catch((err) => {
                console.warn(`[Hold] Audio play failed on unhold:`, err);
              });
              console.log(`[Hold] Audio element resumed for session ${normalizedSession.id}`);
            }
          }
        },
        onReject: (response) => {
          console.warn(`Re-INVITE rejected for session ${normalizedSession.id}:`, response);
        },
      },
    });
  } catch (error) {
    console.error(`Failed to ${hold ? 'hold' : 'resume'} session ${normalizedSession.id}:`, error);
    throw error;
  }
}

export async function acceptIncomingCall(sessionId) {
  // Get current sessions from Redux store
  const allSessions = store.getState().call.sessions || [];
  const normalizedSession = getSessionById(sessionId);

  if (!normalizedSession) {
    console.warn(`No session found for ID: ${sessionId}`);
    return;
  }

  const session = normalizedSession.rawSession || normalizedSession;

  // Validate session state before accepting
  if (!(session.state === SessionState.Initial || session.state === SessionState.Created)) {
    console.warn(`Session not in Initial/Created state but in ${session.state}`);
    return;
  }

  const audioElement = document.getElementById(`call-audio-${sessionId}`);
  if (!audioElement) {
    console.warn(`Audio element not found for session: ${sessionId}`);
    return;
  }

  // Stop incoming ringtone for this call
  stopRingtone();

  try {
    // Put all other established sessions on hold and pause their audio elements
    // eslint-disable-next-line no-restricted-syntax
    for (const s of allSessions) {
      if (s.id !== sessionId && s.rawSession?.state === SessionState.Established) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await holdSession(s, true);
          const otherAudio = document.getElementById(`call-audio-${s.id}`);
          if (otherAudio) {
            otherAudio.pause();
            // console.log(`Paused audio for held session ${s.id}`);
          }
        } catch (err) {
          console.warn(`Failed to put session ${s.id} on hold`, err);
        }
      }
    }

    // Request mic permission for accepted call
    await navigator.mediaDevices.getUserMedia({ audio: true });
    // if call is established then hide keyboard
    if (SessionState.Established) {
      console.log('accepted12345', store);
      store.dispatch(setShowKeyboard(false));
    }
    // Add state change listener on accepted session
    session.stateChange.addListener((newState) => {
      // console.log(`Session ${sessionId} state changed to ${newState}`);

      switch (newState) {
        case SessionState.Establishing:
          store.dispatch(updateSession({ id: sessionId, state: CALL_STATUS.CONNECTING }));
          break;

        case SessionState.Established:
          store.dispatch(
            updateSession({
              id: sessionId,
              state: CALL_STATUS.ESTABLISHED,
              incomingCallAccepted: true,
              startTime: new Date().toISOString(),
            })
          );
          break;

        case SessionState.Terminated:
          store.dispatch(updateSession({ id: sessionId, state: CALL_STATUS.TERMINATED }));
          store.dispatch(removeSession(sessionId));
          break;

        default:
          console.warn('Unhandled session state:', newState);
      }
    });

    // Accept the incoming call with mic constraints
    await session.accept({
      sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false } },
    });

    // console.log('Call accepted');

    // Attach remote audio stream to audio element
    const pc = session.sessionDescriptionHandler.peerConnection;
    const remoteStream = new MediaStream();

    pc.getReceivers().forEach((receiver) => {
      if (receiver.track?.kind === 'audio') {
        remoteStream.addTrack(receiver.track);
      }
    });

    audioElement.srcObject = remoteStream;
    await audioElement.play();
  } catch (error) {
    console.error('Failed to accept call:', error);
    store.dispatch(
      updateSession({
        id: sessionId,
        sessionStatus: 'Failed to connect',
        state: CALL_STATUS.FAILED,
      })
    );
  }
}

/* ----------------------------------------------------------------------------------------------- */

/* ---------------------------------------------------
   Outgoing Call
--------------------------------------------------- */
export async function outgoingCall(targetNumber) {
  debugLog('OUTGOING', `📞 Initiating outgoing call to: ${targetNumber}`);

  if (!checkUserRegistered()) {
    debugLog('OUTGOING', '✗ SIP service not registered');
    showToast('SIP service not registered', 'warn');
    return;
  }

  // Check transport connection before making call
  if (!isTransportConnected()) {
    debugLog('OUTGOING', '✗ WebSocket transport not connected');
    showToast('Cannot make call: Connection lost. Attempting to reconnect...', 'error');
    attemptReconnect('Outgoing call attempted with disconnected transport');
    return;
  }

  try {
    debugLog('OUTGOING', 'Requesting audio permissions...');
    const permissionStream = await requestAudioPermissions();
    if (permissionStream) {
      permissionStream.getTracks().forEach((track) => track.stop());
    }
    const state = store.getState();
    const existingSessions = state.call.sessions || [];
    const establishedSessions = existingSessions.filter(
      (s) => s.state === CALL_STATUS.ESTABLISHED && !s.isOnHold
    );

    if (establishedSessions.length > 0) {
      debugLog(
        'OUTGOING',
        `Auto-holding ${establishedSessions.length} existing call(s) before new outgoing call`
      );
      // eslint-disable-next-line no-restricted-syntax
      for (const session of establishedSessions) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await holdSessionById(session.id, true);
          debugLog('OUTGOING', `Put session ${session.id} on hold`);
        } catch (err) {
          console.warn(`Failed to hold session ${session.id}:`, err);
        }
      }
    }

    const targetURI = UserAgent.makeURI(`sip:${targetNumber}@${configFromBackend.sip_domain}`);
    const inviter = new Inviter(userAgentInstance, targetURI, {
      sessionDescriptionHandlerOptions: {
        constraints: { audio: true, video: false },
      },
    });

    const normalizedSession = normalizeSession(inviter, CALL_TYPE.OUTGOING);
    store.dispatch(addSession(normalizedSession));
    store.dispatch(setDialNumber(''));
    store.dispatch(setShowKeyboard(false));

    // CNAM lookup to get callee name (async - won't block UI)
    if (targetNumber) {
      lookupCNAM(targetNumber).then((cnamData) => {
        if (cnamData?.name) {
          store.dispatch(
            updateSession({
              id: inviter.id,
              callerName: cnamData.name,
              contactUuid: cnamData.uuid || null,
            })
          );
        }
      });
    }

    const audioElemId = `call-audio-${inviter.id}`;
    // Handles SIP responses: 180 (ringing), 183 (progress), 486 (busy), etc.
    const requestDelegate = {
      onTrying: (response) => {
        // console.log('[SIP] onTrying', response.message.statusCode);
      },
      onProgress: (response) => {
        const code = response.message.statusCode;
        // console.log('[SIP] onProgress', code);
        if (code === 180 || code === 183) {
          playTone('early', audioElemId);
        }
      },
      onAccept: (response) => {
        // console.log('[SIP] onAccept', response.message.statusCode);
        // onAccept just keyboard show false
        if (SessionState.Established) {
          console.log('accepted12345', store);
          store.dispatch(setShowKeyboard(false));
        }
      },
      onReject: (response) => {
        const code = response.message.statusCode;
        stopTone(audioElemId);

        if (code === 486) {
          playTone('busy', audioElemId);
        } else {
          playTone('failed', audioElemId);
        }

        // Check for 4XX or 5XX responses and parse Reason header
        if (code >= 400) {
          const reasonText = parseReasonHeader(response);
          const { reasonPhrase } = response.message;

          // Show error message with Reason header text if available
          if (reasonText) {
            showToast(`Call failed: ${reasonText}`, 'error');
          } else if (reasonPhrase) {
            showToast(`Call failed: ${reasonPhrase} (${code})`, 'error');
          } else {
            showToast(`Call failed with status ${code}`, 'error');
          }
        }

        // Session will be cleaned up in stateChange 'Terminated'
      },
    };

    // Handles session state transitions: CONNECTING, ESTABLISHED, TERMINATED, etc.
    inviter.stateChange.addListener((newState) => {
      // console.log('[SIP] Session state:', newState, SessionState[newState]);

      switch (newState) {
        case SessionState.Initial:
          store.dispatch(updateSession({ id: inviter.id, state: CALL_STATUS.CONNECTING }));
          break;

        case SessionState.Establishing:
          store.dispatch(updateSession({ id: inviter.id, state: CALL_STATUS.RINGING }));
          break;

        case SessionState.Established: {
          stopTone(audioElemId);

          store.dispatch(
            updateSession({
              id: inviter.id,
              state: CALL_STATUS.ESTABLISHED,
              startTime: new Date().toISOString(),
            })
          );

          const { sessionDescriptionHandler } = inviter;
          if (!sessionDescriptionHandler) return;

          const pc = sessionDescriptionHandler.peerConnection;
          const remoteStream = new MediaStream();
          pc.getReceivers().forEach((receiver) => {
            if (receiver.track?.kind === 'audio') {
              remoteStream.addTrack(receiver.track);
            }
          });

          let audioElem = document.getElementById(audioElemId);
          if (!audioElem) {
            audioElem = document.createElement('audio');
            audioElem.id = audioElemId;
            document.body.appendChild(audioElem);
          }

          audioElem.srcObject = remoteStream;
          audioElem.play().catch((err) => {
            console.error('Audio playback failed:', err);
          });

          break;
        }

        case SessionState.Terminated:
          stopTone(audioElemId);
          store.dispatch(
            updateSession({
              id: inviter.id,
              state: CALL_STATUS.TERMINATED,
              endTime: new Date().toISOString(),
            })
          );
          store.dispatch(removeSession(inviter.id));
          break;

        default:
        // console.log('[SIP] Unhandled state:', SessionState[newState]);
      }
    });

    await inviter.invite({ requestDelegate });
  } catch (err) {
    console.error('Call failed:', err);
    showToast(`Call failed: ${err.message}`, 'error');
  }
}

/* ---------------------------------------------------
   Call Control: End, Mute, Hold
--------------------------------------------------- */

/**
 * Terminates all active SIP sessions and cleans up resources
 * Should be called on user logout to prevent orphaned calls
 */
export function terminateAllSessions() {
  try {
    debugLog('CLEANUP', 'Terminating all active sessions...');

    const state = store.getState();
    const allSessions = state.call.sessions || [];
    const incomingCalls = state.call.incomingCalls || [];

    debugLog(
      'CLEANUP',
      `Found ${allSessions.length} active sessions and ${incomingCalls.length} incoming calls`
    );

    // Terminate all active sessions
    allSessions.forEach((session) => {
      try {
        const { rawSession } = session;
        if (rawSession) {
          const { state: sessionState } = rawSession;

          // Skip if already terminated or terminating
          if (
            sessionState === SessionState.Terminated ||
            sessionState === SessionState.Terminating
          ) {
            debugLog(
              'CLEANUP',
              `Skipping session ${session.id} - already in ${sessionState} state`
            );
            return;
          }
          if (rawSession.sessionDescriptionHandler?.peerConnection) {
            const pc = rawSession.sessionDescriptionHandler.peerConnection;

            pc.getSenders().forEach((sender) => {
              if (sender.track) {
                sender.track.stop();
              }
            });

            pc.getReceivers().forEach((receiver) => {
              if (receiver.track) {
                receiver.track.stop();
              }
            });
          }

          if (typeof rawSession.bye === 'function' && sessionState === SessionState.Established) {
            debugLog('CLEANUP', `Sending BYE for established session ${session.id}`);
            rawSession.bye();
          } else if (
            typeof rawSession.cancel === 'function' &&
            sessionState === SessionState.Initial
          ) {
            debugLog('CLEANUP', `Cancelling session ${session.id}`);
            rawSession.cancel();
          } else if (typeof rawSession.terminate === 'function') {
            debugLog('CLEANUP', `Terminating session ${session.id} (state: ${sessionState})`);
            rawSession.terminate();
          } else if (typeof rawSession.dispose === 'function') {
            debugLog('CLEANUP', `Disposing session ${session.id}`);
            rawSession.dispose();
          }
        }
      } catch (err) {
        console.error(`Error terminating session ${session.id}:`, err);
      }
    });

    // Reject all incoming calls
    incomingCalls.forEach((call) => {
      try {
        if (call.rawSession && typeof call.rawSession.reject === 'function') {
          const { state: callState } = call.rawSession;

          // Only reject if session is not already terminated or terminating
          if (callState !== SessionState.Terminated && callState !== SessionState.Terminating) {
            debugLog('CLEANUP', `Rejecting incoming call ${call.id} (state: ${callState})`);
            call.rawSession.reject();
          } else {
            debugLog(
              'CLEANUP',
              `Skipping reject for call ${call.id} - already in ${callState} state`
            );
          }
        }
      } catch (err) {
        console.error(`Error rejecting incoming call ${call.id}:`, err);
      }
    });

    // Stop ringtone
    stopRingtone?.();

    // Clean up connection monitoring
    cleanupConnectionMonitoring();

    // Stop UserAgent
    if (userAgentInstance) {
      debugLog('CLEANUP', 'Stopping UserAgent...');
      userAgentInstance.stop().catch((err) => {
        console.error('Error stopping UserAgent:', err);
      });
      userAgentInstance = null;
    }

    // Clear registerer
    registerer = null;

    // Clear Redux state
    store.dispatch({ type: 'call/clearSessions' });
    store.dispatch({ type: 'call/setRegisterUA', payload: { status: false, error: null } });

    debugLog('CLEANUP', 'All sessions terminated and resources cleaned up');
  } catch (error) {
    console.error('Error during terminateAllSessions:', error);
  }
}

export function endCall(sessionId) {
  if (!sessionId || !checkUserRegistered()) return;

  const sessionObj = getSessionById(sessionId)?.rawSession;

  if (!sessionObj) {
    console.warn('No session found for ID:', sessionId);
    return;
  }

  try {
    // Handle conference cleanup before ending the call
    // Don't await - let it run in background so it doesn't block call termination
    handleConferenceSessionEnd(sessionId).catch((confError) => {
      console.error('Error during conference cleanup:', confError);
      // Conference cleanup failed, but call will still terminate
    });

    // CRITICAL FIX: Stop all media tracks (microphone) before ending the call
    if (sessionObj.sessionDescriptionHandler?.peerConnection) {
      const pc = sessionObj.sessionDescriptionHandler.peerConnection;

      // Stop all sender tracks (local microphone)
      pc.getSenders().forEach((sender) => {
        if (sender.track) {
          sender.track.stop();
        }
      });

      // Stop all receiver tracks (remote audio)
      pc.getReceivers().forEach((receiver) => {
        if (receiver.track) {
          receiver.track.stop();
        }
      });
    }

    // Also stop original audio track if stored (from conference)
    if (sessionObj.originalAudioTrack) {
      sessionObj.originalAudioTrack.stop();
    }

    const { state: sessionState } = sessionObj;

    if (typeof sessionObj.bye === 'function' && sessionState === 'Established') {
      sessionObj.bye();
    } else if (typeof sessionObj.cancel === 'function' && sessionState === 'Initial') {
      sessionObj.cancel();
    } else if (typeof sessionObj.reject === 'function' && sessionState === 'Initial') {
      sessionObj.reject();
    } else if (typeof sessionObj.terminate === 'function') {
      sessionObj.terminate();
    } else if (typeof sessionObj.dispose === 'function') {
      sessionObj.dispose();
    } else {
      console.warn('Unknown session state or method not found:', sessionObj);
    }
  } catch (err) {
    console.error('Error ending call:', err);
  } finally {
    // Remove the ended session
    store.dispatch(removeSession(sessionId));
    store.dispatch(removeIncomingCall(sessionId));
    stopRingtone?.();

    // Now update other sessions that might be related by transfer
    const state = store.getState();
    const allSessions = state.call.sessions;

    allSessions.forEach((s) => {
      let needsUpdate = false;
      const updates = {};

      // If this session was transferring to the ended call, clear transfer info
      if (s.transferTargetId === sessionId) {
        updates.transferTargetId = null;
        updates.isBeingTransferred = false;
        updates.transferState = null;
        needsUpdate = true;
      }

      // If this ended session had a transferTargetId, find that target and clear its transfer info as well
      if (s.id === sessionId && s.transferTargetId) {
        const targetSession = allSessions.find((ts) => ts.id === s.transferTargetId);
        if (targetSession) {
          store.dispatch(
            updateSession({
              id: targetSession.id,
              transferTargetId: null,
              isBeingTransferred: false,
              transferState: null,
            })
          );
        }
      }

      if (needsUpdate) {
        store.dispatch(
          updateSession({
            id: s.id,
            ...updates,
          })
        );
      }
    });
  }
}

/* ---------------------------------------------------
  Blind Transfer
--------------------------------------------------- */

export async function blindTransferById(sessionId, targetNumber) {
  // console.log('current session id', sessionId, targetNumber);

  const session = getSessionById(sessionId)?.rawSession;

  if (!session) {
    showToast('No valid session found for transfer.', 'warn');
    return;
  }

  if (session.state !== SessionState.Established) {
    showToast('Session is not active.', 'warn');
    return;
  }

  try {
    const targetURI = UserAgent.makeURI(`sip:${targetNumber}@${configFromBackend.sip_domain}`);
    if (!targetURI) {
      throw new Error('Invalid target URI');
    }

    const referRequest = await session.refer(targetURI);

    referRequest.delegate = {
      onAccept: () => {
        showToast(`Call transferred to ${targetNumber}`, 'success');
        // console.log(`✅ Call was successfully transferred to ${targetNumber}`);
      },
      onReject: () => {
        showToast(`Transfer to ${targetNumber} was rejected`, 'error');
        // console.error(`❌ Transfer to ${targetNumber} was rejected`);
      },
    };

    session.bye(); // Ends the local call session
    // store.dispatch(resetState());
  } catch (error) {
    console.error('Error performing blind transfer:', error);
    showToast(`Transfer failed: ${error.message}`, 'error');
  }
}

/* ---------------------------------------------------
   Helper
--------------------------------------------------- */

/* -------------------------------Individual Level Functionality ----------------------------- */
export async function holdSessionById(sessionId, hold = true, skipAutoHold = false) {
  const normalizedSession = getSessionById(sessionId);
  const session = normalizedSession?.rawSession;

  if (!session) throw new Error('Session not found');

  console.log(
    `[Hold By ID] ${hold ? 'Holding' : 'Unholding'} session ${sessionId}${skipAutoHold ? ' (skipping auto-hold)' : ''}`
  );

  // AUTO-HOLD: When resuming a call, put all other active calls on hold
  // Skip this behavior when skipAutoHold=true (used during conference merge)
  if (!hold && !skipAutoHold) {
    const state = store.getState();
    const allSessions = state.call.sessions || [];
    const otherActiveSessions = allSessions.filter(
      (s) =>
        s.id !== sessionId &&
        s.state === CALL_STATUS.ESTABLISHED &&
        !s.isOnHold &&
        !isSessionInConference(s) // Don't auto-hold conference sessions
    );

    if (otherActiveSessions.length > 0) {
      console.log(
        `[Hold By ID] Auto-holding ${otherActiveSessions.length} other active session(s) before resuming ${sessionId}`
      );
      // eslint-disable-next-line no-restricted-syntax
      for (const otherSession of otherActiveSessions) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await holdSessionById(otherSession.id, true, false);
          console.log(`[Hold By ID] ✓ Auto-held session ${otherSession.id}`);
        } catch (err) {
          console.warn(`[Hold By ID] Failed to auto-hold session ${otherSession.id}:`, err);
        }
      }
    }
  }

  const pc = session.sessionDescriptionHandler.peerConnection;

  // Toggle audio sender tracks enabled/disabled
  pc.getSenders().forEach((sender) => {
    if (sender.track && sender.track.kind === 'audio') {
      sender.track.enabled = !hold; // Disable tracks on hold, enable on unhold
      console.log(
        `[Hold By ID] Sender track ${hold ? 'disabled' : 'enabled'} for session ${sessionId}`
      );
    }
  });

  // SDP modifier to change media direction for hold/unhold
  const sdpModifier = (description) => {
    description.sdp = description.sdp.replace(
      /a=sendrecv|a=sendonly|a=inactive/g,
      hold ? 'a=inactive' : 'a=sendrecv'
    );
    return Promise.resolve(description);
  };

  try {
    await session.invite({
      sessionDescriptionHandlerModifiers: [sdpModifier],
      requestDelegate: {
        onAccept: () => {
          console.log(
            `[Hold By ID] ✓ Session ${sessionId} ${hold ? 'held' : 'unheld'} - SDP accepted`
          );

          // Update Redux store session isOnHold flag
          store.dispatch(updateSession({ id: session.id, isOnHold: hold }));

          // Handle audio element playback
          const audioElement = document.getElementById(`call-audio-${session.id}`);
          if (hold) {
            if (audioElement) {
              audioElement.pause();
              console.log(`[Hold By ID] Audio element paused for session ${sessionId}`);
            }
          } else if (audioElement) {
            audioElement.play().catch((err) => {
              console.warn('[Hold By ID] Audio play failed on unhold:', err);
            });
            console.log(`[Hold By ID] Audio element playing for session ${sessionId}`);
          }

          // CRITICAL FIX: After unhold, ensure tracks are fresh and enabled
          if (!hold) {
            console.log(
              `[Hold By ID] 🔄 Refreshing audio track after unhold for session ${sessionId}`
            );
            navigator.mediaDevices
              .getUserMedia({ audio: true })
              .then((stream) => {
                const newTrack = stream.getAudioTracks()[0];
                const sender = pc.getSenders().find((s) => s.track?.kind === 'audio');
                if (sender) {
                  sender.replaceTrack(newTrack).then(() => {
                    console.log(`[Hold By ID] ✓ Audio track refreshed for session ${sessionId}`);
                  });
                }
              })
              .catch((err) => {
                console.warn('[Hold By ID] Failed to get audio track after unhold:', err);
              });
          }
        },
        onReject: (response) => {
          console.warn(`[Hold By ID] ✗ Re-INVITE rejected for session ${sessionId}:`, response);
        },
      },
    });

    // CRITICAL FIX: Wait for SDP renegotiation to complete
    console.log(
      `[Hold By ID] ⏳ Waiting for ${hold ? 'hold' : 'unhold'} SDP renegotiation (500ms)...`
    );
    await new Promise((resolve) => setTimeout(resolve, 500));
    console.log(`[Hold By ID] ✓ SDP renegotiation completed for session ${sessionId}`);
  } catch (error) {
    console.error(
      `[Hold By ID] ✗ ${hold ? 'Hold' : 'Unhold'} failed for session ${sessionId}:`,
      error
    );
    throw error;
  }
} // Your SIP state to UI status mapper

/* ------------------------------------------------------------------------------------------------ */

// Initiate the attended transfer call
export const attendedTransfer = async (sourceSession, transferNumber) => {
  if (!sourceSession || !transferNumber) {
    console.warn('attendedTransfer called with invalid arguments');
    return null;
  }

  try {
    const { userAgent } = sourceSession.rawSession || {};

    if (!userAgent) {
      console.error('UserAgent not found on sourceSession.rawSession');
      return null;
    }

    const targetURI = userAgent.configuration.uri.clone();
    targetURI.user = transferNumber;

    // Optional: media constraints (get microphone permission)
    const constraints = { audio: true, video: false };
    const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);

    const inviterOptions = {
      sessionDescriptionHandlerOptions: { constraints },
      requestDelegate: {
        onReject: (response) => {
          console.warn('Transfer call rejected:', response.message.reasonPhrase);
          store.dispatch(
            updateSession({
              id: sourceSession.id,
              transferState: 'rejected',
            })
          );
        },
      },
    };

    const inviter = new Inviter(userAgent, targetURI, inviterOptions);

    await inviter.invite();

    return inviter;
  } catch (error) {
    console.error('attendedTransfer error:', error);
    return null;
  }
};

// Handle new transfer target session lifecycle & update Redux
export async function createTransferCall(sourceSession, transferNumber) {
  if (!sourceSession?.rawSession) {
    console.warn('Invalid sourceSession');
    return null;
  }

  if (!transferNumber) {
    console.warn('No transfer number provided');
    return null;
  }

  try {
    // Request permission but stop the stream - Inviter will create its own stream
    const permissionStream = await requestAudioPermissions();
    if (permissionStream) {
      permissionStream.getTracks().forEach((track) => track.stop());
    }

    const { userAgent } = sourceSession.rawSession;

    const targetURI = userAgent.configuration.uri.clone();
    targetURI.user = transferNumber;

    const inviter = new Inviter(userAgent, targetURI, {
      sessionDescriptionHandlerOptions: {
        constraints: { audio: true, video: false },
      },
    });

    // Prepare Redux session object and add to store
    const normalizedSession = normalizeSession(inviter, CALL_TYPE.OUTGOING);

    store.dispatch(addSession(normalizedSession));

    // Link this transfer call to the source session
    store.dispatch(
      updateSession({
        id: sourceSession.id,
        transferTargetId: normalizedSession.id,
      })
    );

    // Similar requestDelegate and stateChange handlers as in outgoingCall()
    const audioElemId = `call-audio-${inviter.id}`;

    const requestDelegate = {
      onTrying: (response) => console.log('[Transfer] onTrying', response.message.statusCode),
      onProgress: (response) => {
        const code = response.message.statusCode;
        // console.log('[Transfer] onProgress', code);
        if (code === 180 || code === 183) playTone('early', audioElemId);
      },
      onAccept: (response) => {
        // console.log('[Transfer] onAccept', response.message.statusCode);
      },
      onReject: (response) => {
        const code = response.message.statusCode;
        // console.log('[Transfer] onReject', code);
        stopTone(audioElemId);
        if (code === 486) playTone('busy', audioElemId);
        else playTone('failed', audioElemId);
      },
    };

    inviter.stateChange.addListener((newState) => {
      switch (newState) {
        case SessionState.Establishing:
          store.dispatch(updateSession({ id: inviter.id, state: CALL_STATUS.RINGING }));
          break;

        case SessionState.Established:
          stopTone(audioElemId);
          store.dispatch(
            updateSession({
              id: inviter.id,
              state: CALL_STATUS.ESTABLISHED,
              startTime: new Date().toISOString(),
            })
          );
          // Set up audio playback similar to your outgoingCall
          // ...
          break;

        case SessionState.Terminated:
          stopTone(audioElemId);
          store.dispatch(
            updateSession({
              id: inviter.id,
              state: CALL_STATUS.TERMINATED,
              endTime: new Date().toISOString(),
            })
          );
          store.dispatch(removeSession(inviter.id));
          break;

        default:
        // console.log('[Transfer] Unhandled state:', SessionState[newState]);
      }
    });

    await inviter.invite({ requestDelegate });

    return normalizedSession;
  } catch (err) {
    console.error('Transfer call failed:', err);
    showToast(`Transfer call failed: ${err.message}`, 'error');
    return null;
  }
}

// Complete attended transfer by sending REFER from sourceSession to transferSession
export const completeAttendedTransfer = async (sourceSessionId) => {
  const state = store.getState();
  const sourceSession = state.call.sessions.find((s) => s.id === sourceSessionId);

  if (!sourceSession?.transferTargetId) {
    console.error('No transfer target found for attended transfer completion');
    return false;
  }

  const targetSession = state.call.sessions.find((s) => s.id === sourceSession.transferTargetId);

  if (!sourceSession?.rawSession || !targetSession?.rawSession) {
    console.error('Missing raw SIP sessions for transfer');
    return false;
  }

  try {
    await sourceSession.rawSession.refer(targetSession.rawSession);

    // console.log('Attended transfer REFER sent');

    store.dispatch(
      updateSession({
        id: sourceSessionId,
        transferTargetId: null,
        transferState: CALL_STATUS.ESTABLISHED,
      })
    );

    return true;
  } catch (error) {
    console.error('Failed to complete attended transfer:', error);
    store.dispatch(
      updateSession({
        id: sourceSessionId,
        transferState: CALL_STATUS.FAILED,
      })
    );
    return false;
  }
};

/* ----------------------------------------------------------------------------------------------- */
/* Individual Level Mute and unmute */
// Mute by session ID
// Mute by session ID
export function muteSessionById(sessionId) {
  const state = store.getState();
  const session = state.call.sessions.find((s) => s.id === sessionId)?.rawSession;

  if (session?.sessionDescriptionHandler?.peerConnection) {
    session.sessionDescriptionHandler.peerConnection.getSenders().forEach((sender) => {
      if (sender.track && sender.track.kind === 'audio') {
        sender.track.enabled = false;
      }
    });

    store.dispatch(updateSession({ id: sessionId, isMuted: true }));
  } else {
    console.warn(`No session found for ID: ${sessionId}`);
  }
}

// Unmute by session ID
export function unmuteSessionById(sessionId) {
  const state = store.getState();
  const session = state.call.sessions.find((s) => s.id === sessionId)?.rawSession;

  if (session?.sessionDescriptionHandler?.peerConnection) {
    session.sessionDescriptionHandler.peerConnection.getSenders().forEach((sender) => {
      if (sender.track && sender.track.kind === 'audio') {
        sender.track.enabled = true;
      }
    });

    store.dispatch(updateSession({ id: sessionId, isMuted: false }));
  } else {
    console.warn(`No session found for ID: ${sessionId}`);
  }
}

/**
 * Conference-aware mute function for host
 * When host is in a conference, mutes only host's mic (A and B can still hear each other)
 * When not in conference, mutes the session normally
 * @param {string} sessionId - Session ID to check for conference status
 */
export async function muteHost(sessionId) {
  const session = getSessionById(sessionId);

  if (!session) {
    console.warn(`No session found for ID: ${sessionId}`);
    return false;
  }

  // Check if session is in a conference
  if (isSessionInConference(session)) {
    // Use conference-specific mute that preserves A-B communication
    const result = await muteHostInConference(session.conferenceGroupId);
    if (result) {
      // Update both sessions in the conference to show host is muted
      const conferenceSessions = getConferenceSessions(session.conferenceGroupId);
      conferenceSessions.forEach((s) => {
        store.dispatch(updateSession({ id: s.id, isHostMuted: true }));
      });
    }
    return result;
  }

  // Not in conference, use regular mute
  muteSessionById(sessionId);
  return true;
}

/**
 * Conference-aware unmute function for host
 * @param {string} sessionId - Session ID to check for conference status
 */
export async function unmuteHost(sessionId) {
  const session = getSessionById(sessionId);

  if (!session) {
    console.warn(`No session found for ID: ${sessionId}`);
    return false;
  }

  // Check if session is in a conference
  if (isSessionInConference(session)) {
    // Use conference-specific unmute
    const result = await unmuteHostInConference(session.conferenceGroupId);
    if (result) {
      // Update both sessions in the conference to show host is unmuted
      const conferenceSessions = getConferenceSessions(session.conferenceGroupId);
      conferenceSessions.forEach((s) => {
        store.dispatch(updateSession({ id: s.id, isHostMuted: false }));
      });
    }
    return result;
  }

  // Not in conference, use regular unmute
  unmuteSessionById(sessionId);
  return true;
}

/**
 * Mute a specific participant in a conference
 * This mutes the participant so nobody can hear them (host, other participants)
 * @param {string} sessionId - Session ID of the participant to mute
 */
export async function muteParticipant(sessionId) {
  const session = getSessionById(sessionId);

  if (!session) {
    console.warn(`No session found for ID: ${sessionId}`);
    return false;
  }

  // Use the conference participant mute function
  const result = await muteConferenceParticipant(sessionId);

  if (result) {
    store.dispatch(updateSession({ id: sessionId, isMuted: true }));
  }

  return result;
}

/**
 * Unmute a specific participant in a conference
 * @param {string} sessionId - Session ID of the participant to unmute
 */
export async function unmuteParticipant(sessionId) {
  const session = getSessionById(sessionId);

  if (!session) {
    console.warn(`No session found for ID: ${sessionId}`);
    return false;
  }

  // Use the conference participant unmute function
  const result = await unmuteConferenceParticipant(sessionId);

  if (result) {
    store.dispatch(updateSession({ id: sessionId, isMuted: false }));
  }

  return result;
}

/* ---------------------------------------------------------------SEND DTMF---------------------------------*/

/**
 * Send a DTMF tone via SIP INFO request to the raw SIP session.
 *
 * @param {Object} sipSession - The raw SIP.js session object.
 * @param {string} tone - The DTMF tone to send (e.g., '1', '2', '*', '#').
 * @param {number} duration - Duration of the tone in milliseconds (default: 1000).
 */
export async function sendDTMF(session, tone, duration = 100, interToneGap = 70) {
  if (!session || !session.rawSession) {
    console.warn('No valid session or rawSession');
    return;
  }

  const validTones = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '#'];
  const toneStr = String(tone); // Ensure it's always a string

  if (!validTones.includes(toneStr)) {
    console.warn(` Invalid DTMF tone: "${tone}". Must be one of 0-9, *, #`);
    return;
  }

  const { rawSession } = session;

  // 1. RTCDTMFSender (ideal for FreeSWITCH)
  try {
    const pc = rawSession.sessionDescriptionHandler.peerConnection;
    const sender = pc.getSenders().find((s) => s.track && s.track.kind === 'audio');
    if (sender?.dtmf) {
      sender.dtmf.insertDTMF(tone, duration, interToneGap);
      console.log(` Sent DTMF "${tone}" via RTCDTMFSender (RFC 2833)`);
      return;
    }
    console.warn('No RTCDTMFSender available');
  } catch (err) {
    console.error(' RTCDTMFSender insertDTMF error:', err);
  }

  // 2. SIP.js .dtmf() fallback
  if (typeof rawSession.dtmf === 'function') {
    try {
      rawSession.dtmf(tone);
      // console.log(`✅ Sent DTMF "${tone}" via session.dtmf()`);
      return;
    } catch (err) {
      console.error(' SIP.js .dtmf() failed:', err);
    }
  }

  // 3. SIP INFO manual fallback
  if (typeof rawSession.info === 'function') {
    try {
      await rawSession.info({
        contentType: 'application/dtmf-relay',
        body: `Signal=${tone}\r\nDuration=${duration}`,
      });
      console.log(` Sent DTMF "${tone}" via SIP INFO`);
    } catch (err) {
      console.error(' SIP INFO fallback failed:', err);
    }
  } else {
    console.warn(' No available DTMF methods');
  }
}

/* ---------------------------------------------------
   Switch Microphone for Active Sessions
--------------------------------------------------- */
export async function switchMicrophone(deviceId) {
  const state = store.getState();
  const activeSessions = state.call.sessions || [];

  if (activeSessions.length === 0) {
    return;
  }

  try {
    // Get new audio stream from selected microphone
    const newStream = await navigator.mediaDevices.getUserMedia({
      audio: { deviceId: { exact: deviceId } },
    });

    const newAudioTrack = newStream.getAudioTracks()[0];
    if (!newAudioTrack) {
      throw new Error('No audio track in new stream');
    }

    // Update microphone for all active established sessions
    let updatedCount = 0;
    // eslint-disable-next-line no-restricted-syntax
    for (const session of activeSessions) {
      const { rawSession } = session;

      // Only update established sessions that have a peer connection
      if (
        rawSession &&
        rawSession.state === SessionState.Established &&
        rawSession.sessionDescriptionHandler?.peerConnection
      ) {
        const pc = rawSession.sessionDescriptionHandler.peerConnection;
        const sender = pc.getSenders().find((s) => s.track?.kind === 'audio');

        if (sender) {
          try {
            // eslint-disable-next-line no-await-in-loop
            await sender.replaceTrack(newAudioTrack);
            updatedCount += 1;
          } catch (err) {
            console.error('Failed to replace audio track:', err);
          }
        }
      }
    }

    if (updatedCount > 0) {
      showToast(`Microphone switched for ${updatedCount} active call(s)`, 'success');
    }
  } catch (error) {
    console.error('Failed to switch microphone:', error);
    showToast(`Failed to switch microphone: ${error.message}`, 'error');
  }
}

/* whisper Listen and Barge */

export const monitorCall = async (mode, targetUUID) => {
  let ext;
  switch (mode) {
    case 'listen':
      ext = `*91${targetUUID}`;
      break;
    case 'whisper':
      ext = `*92${targetUUID}`;
      break;
    case 'barge':
      ext = `*93${targetUUID}`;
      break;
    default:
      console.error('Invalid monitor mode');
      return;
  }

  const uriString = `sip:${ext}@${configFromBackend.sip_domain}`;
  const targetURI = UserAgent.makeURI(uriString);

  if (!targetURI) {
    console.error('Invalid SIP URI:', uriString);
    return;
  }

  const inviter = new Inviter(userAgentInstance, targetURI);

  try {
    const session = await inviter.invite();
    session.id = `monitor-${targetUUID}-${mode}`;

    const setupRemoteStream = () => {
      console.log('Setting up remote stream for monitoring...');
      const sdh = session.sessionDescriptionHandler;
      const remoteStream = new MediaStream();

      if (sdh?.peerConnection) {
        sdh.peerConnection.getReceivers().forEach((receiver) => {
          if (receiver.track) remoteStream.addTrack(receiver.track);
        });
      }

      // Optional audio playback
      const audio = new Audio();
      audio.srcObject = remoteStream;
      audio.play().catch((err) => {
        console.warn('Audio play failed:', err);
      });

      // Dispatch only serializable values
      store.dispatch(
        addMonitorSession({
          id: session.id,
          status: 'established',
          mode,
          targetUUID,
          // remoteStream and sipSession excluded if not serializable
        })
      );

      console.log('Dispatched monitor session:', session.id);
    };

    // Session state monitoring
    const waitForEstablished = () => {
      const check = () => {
        if (
          session.state === SessionState.Established ||
          session.status === 6 // backward compatibility with SIP.js 0.20
        ) {
          setupRemoteStream();
        } else {
          setTimeout(check, 250);
        }
      };
      check();
    };

    waitForEstablished();

    session.delegate = {
      onBye: () => {
        console.log('Monitor session ended:', session.id);
        store.dispatch(removeMonitorSession(session.id));
      },
    };

    console.log(`${mode} started on UUID: ${targetUUID}`);
  } catch (err) {
    console.error(`Failed to ${mode} monitor:`, err);
  }
};

/* ---------------------------------------------------
   Conference Calling - Merge Two Active Calls
--------------------------------------------------- */

/**
 * Merge multiple active sessions into a multi-party conference call
 * Supports 2-5 participants + host
 * @param {...string} sessionIds - Session IDs to merge (can be array or individual arguments)
 * @returns {Promise<boolean>} - Success status
 */
export async function mergeCalls(...sessionIds) {
  // Support both array and individual arguments
  const sessionIdArray = Array.isArray(sessionIds[0]) ? sessionIds[0] : sessionIds;

  console.log('═══════════════════════════════════════════════════');
  debugLog('CONFERENCE', `🎯 Attempting to merge ${sessionIdArray.length} calls`);
  console.log('[CONFERENCE] Session IDs:', sessionIdArray);

  try {
    // Get all sessions
    const sessions = sessionIdArray.map((id) => getSessionById(id)).filter(Boolean);

    console.log(`[CONFERENCE] Retrieved ${sessions.length} sessions from Redux`);

    if (sessions.length < 2) {
      showToast('Cannot merge: Need at least 2 sessions', 'error');
      console.error('[CONFERENCE] ✗ Not enough sessions');
      return false;
    }

    if (sessions.length !== sessionIdArray.length) {
      showToast('Cannot merge: One or more sessions not found', 'error');
      console.error('[CONFERENCE] ✗ Some sessions not found in Redux');
      console.error('[CONFERENCE]   Requested:', sessionIdArray);
      console.error(
        '[CONFERENCE]   Found:',
        sessions.map((s) => s.id)
      );
      return false;
    }

    console.log(`[CONFERENCE] ✓ All ${sessions.length} sessions found`);
    sessions.forEach((session, idx) => {
      console.log(`[CONFERENCE] Session ${idx}:`, {
        id: session.id,
        callerNumber: session.callerNumber,
        state: session.state,
        isConference: session.isConference,
        isOnHold: session.isOnHold,
        hasPeerConnection: !!session.rawSession?.sessionDescriptionHandler?.peerConnection,
      });
    });

    // Validate sessions can be merged
    const validation = canMergeSessions(sessions);
    if (!validation.valid) {
      showToast(`Cannot merge calls: ${validation.reason}`, 'error');
      console.error(`[CONFERENCE] ✗ Validation failed: ${validation.reason}`);
      return false;
    }

    console.log('[CONFERENCE] ✓ Validation passed');

    // Check if sessions have proper SIP.js rawSession
    for (let i = 0; i < sessions.length; i += 1) {
      const session = sessions[i];
      if (!session.rawSession || !session.rawSession.sessionDescriptionHandler) {
        console.error(
          `[CONFERENCE] ✗ Session ${i} (${session.id}) missing rawSession or sessionDescriptionHandler`
        );
        throw new Error(`Session ${session.id} missing rawSession or sessionDescriptionHandler`);
      }
      console.log(`[CONFERENCE] ✓ Session ${i} has valid peer connection`);
    }

    // Take all sessions off hold if they are on hold
    // CRITICAL FIX: Pass skipAutoHold=true to prevent auto-holding other calls during merge
    let unholdCount = 0;
    // eslint-disable-next-line no-restricted-syntax
    for (const session of sessions) {
      if (session.isOnHold) {
        console.log(`[CONFERENCE] Taking session ${session.id} off hold for merge...`);
        // eslint-disable-next-line no-await-in-loop
        await holdSessionById(session.id, false, true); // skipAutoHold=true
        unholdCount += 1;
      }
    }
    if (unholdCount > 0) {
      console.log(
        `[CONFERENCE] ✓ Took ${unholdCount} sessions off hold (without auto-holding others)`
      );
    }

    // CRITICAL FIX: Increased wait time for hold state to fully settle
    // This ensures SDP renegotiation completes before we start mixing audio
    console.log('[CONFERENCE] ⏳ Waiting for hold state to settle (1500ms)...');
    await new Promise((resolve) => setTimeout(resolve, 1500));

    // Create audio mix for all sessions
    console.log(`[CONFERENCE] 🎵 Creating audio mix for ${sessions.length} sessions...`);
    const audioMix = await createConferenceAudioMix(sessions);

    if (!audioMix.success) {
      console.error('[CONFERENCE] ✗ Audio mix creation failed:', audioMix.error);
      throw new Error(audioMix.error || 'Failed to create audio mix');
    }

    console.log(`[CONFERENCE] ✓ Audio mix created: ${audioMix.mixedTracks.length} tracks`);
    audioMix.mixedTracks.forEach((mixData, idx) => {
      console.log(`[CONFERENCE]   Mix ${idx}: sessionId=${mixData.sessionId}`);
    });

    // Replace audio tracks on all sessions
    console.log(
      `[CONFERENCE] 🔄 Replacing audio tracks for ${audioMix.mixedTracks.length} sessions...`
    );
    const replaceResults = [];

    for (let i = 0; i < audioMix.mixedTracks.length; i += 1) {
      const mixData = audioMix.mixedTracks[i];
      const session = sessions.find((s) => s.id === mixData.sessionId);

      if (!session) {
        console.error(`[CONFERENCE] ✗ Session not found for mix data: ${mixData.sessionId}`);
        replaceResults.push(false);
      } else {
        console.log(
          `[CONFERENCE]   Replacing track ${i + 1}/${audioMix.mixedTracks.length} for session ${session.id}...`
        );
        // eslint-disable-next-line no-await-in-loop
        const result = await replaceSessionAudioTrack(session, mixData.mixedTrack);
        replaceResults.push(result);

        if (result) {
          // Store original track on session for later restoration
          session.rawSession.originalAudioTrack = audioMix.originalTracks[mixData.sessionId];
          console.log(`[CONFERENCE]   ✓ Track replaced for session ${session.id}`);
        } else {
          console.error(`[CONFERENCE]   ✗ Failed to replace track for session ${session.id}`);
        }
      }
    }

    if (replaceResults.some((r) => !r)) {
      console.error('[CONFERENCE] ✗ Some track replacements failed');
      throw new Error('Failed to replace one or more audio tracks');
    }

    console.log(`[CONFERENCE] ✓ All ${replaceResults.length} tracks replaced successfully`);

    // CRITICAL FIX: Post-merge verification - ensure all audio elements are playing
    console.log('[CONFERENCE] 🔊 Verifying audio elements are playing...');
    // eslint-disable-next-line no-restricted-syntax
    for (const session of sessions) {
      const audioElement = document.getElementById(`call-audio-${session.id}`);
      if (audioElement) {
        if (audioElement.paused) {
          console.log(
            `[CONFERENCE] ⚠️  Audio element paused for session ${session.id}, resuming...`
          );
          try {
            // eslint-disable-next-line no-await-in-loop
            await audioElement.play();
            console.log(`[CONFERENCE] ✓ Audio resumed for session ${session.id}`);
          } catch (playError) {
            console.warn(`[CONFERENCE] Audio play error for session ${session.id}:`, playError);
          }
        } else {
          console.log(`[CONFERENCE] ✓ Audio already playing for session ${session.id}`);
        }
      } else {
        console.warn(`[CONFERENCE] ⚠️  No audio element found for session ${session.id}`);
      }
    }

    // Generate conference group ID
    const conferenceGroupId = generateConferenceId();
    console.log(`[CONFERENCE] Generated conference ID: ${conferenceGroupId}`);

    // Store conference group ID on all sessions
    sessions.forEach((session) => {
      session.rawSession.conferenceGroupId = conferenceGroupId;
    });

    // Prepare audio data for Redux (only serializable data)
    const audioDataForRedux = {};
    audioMix.mixedTracks.forEach((mixData) => {
      audioDataForRedux[`mixed_${mixData.sessionId}`] = {
        id: mixData.mixedTrack.id,
        label: mixData.mixedTrack.label,
      };
    });

    Object.keys(audioMix.originalTracks).forEach((sessionId) => {
      audioDataForRedux[`original_${sessionId}`] = {
        id: audioMix.originalTracks[sessionId].id,
        label: audioMix.originalTracks[sessionId].label,
      };
    });

    // Create conference in Redux
    console.log(`[CONFERENCE] 📦 Dispatching createConference to Redux...`);
    console.log(`[CONFERENCE]   Conference ID: ${conferenceGroupId}`);
    console.log(`[CONFERENCE]   Session IDs: ${sessionIdArray.join(', ')}`);

    store.dispatch(
      createConference({
        groupId: conferenceGroupId,
        sessionIds: sessionIdArray,
        audioData: audioDataForRedux,
      })
    );

    console.log('[CONFERENCE] ✓ Redux state updated');

    // Verify Redux state was updated
    const updatedSessions = store.getState().call.sessions;
    console.log('[CONFERENCE] 📊 Verifying Redux state after dispatch:');
    sessionIdArray.forEach((sessionId) => {
      const session = updatedSessions.find((s) => s.id === sessionId);
      if (session) {
        console.log(`[CONFERENCE]   Session ${sessionId}:`, {
          isConference: session.isConference,
          conferenceGroupId: session.conferenceGroupId,
        });
      } else {
        console.error(`[CONFERENCE]   ✗ Session ${sessionId} not found in Redux!`);
      }
    });

    logConferenceEvent('MERGE_COMPLETE', {
      conferenceId: conferenceGroupId,
      sessions: sessionIdArray,
      participantCount: sessions.length,
    });

    showToast(`Conference active with ${sessions.length} participants`, 'success');
    console.log('[CONFERENCE] ✓✓✓ Conference merge completed successfully');
    console.log('═══════════════════════════════════════════════════');

    return true;
  } catch (error) {
    console.error('═══════════════════════════════════════════════════');
    console.error('[CONFERENCE] ✗✗✗ Error merging calls:', error);
    console.error('[CONFERENCE] Error stack:', error.stack);
    showToast(`Failed to merge calls: ${error.message}`, 'error');
    console.error('═══════════════════════════════════════════════════');
    return false;
  }
}

/**
 * End a conference and restore original audio for both sessions
 * @param {string} conferenceGroupId - Conference group identifier
 * @returns {Promise<boolean>} - Success status
 */
export async function endConferenceCall(conferenceGroupId) {
  debugLog('CONFERENCE', `Ending conference: ${conferenceGroupId}`);

  try {
    const conferenceSessions = getConferenceSessions(conferenceGroupId);

    if (conferenceSessions.length === 0) {
      debugLog('CONFERENCE', 'No sessions found in conference');
      return false;
    }

    debugLog('CONFERENCE', `Found ${conferenceSessions.length} sessions in conference`);

    // Restore original audio for each session
    // eslint-disable-next-line no-restricted-syntax
    for (const session of conferenceSessions) {
      if (session.rawSession?.originalAudioTrack) {
        debugLog('CONFERENCE', `Restoring original audio for session: ${session.id}`);
        // eslint-disable-next-line no-await-in-loop
        await restoreOriginalAudio(session, session.rawSession.originalAudioTrack);

        // Clean up stored references
        delete session.rawSession.originalAudioTrack;
        delete session.rawSession.conferenceGroupId;
      }
    }

    // End conference in Redux
    store.dispatch(endConference(conferenceGroupId));

    logConferenceEvent('CONFERENCE_ENDED', { conferenceId: conferenceGroupId });
    showToast('Conference ended', 'info');
    debugLog('CONFERENCE', '✓ Conference ended successfully');

    return true;
  } catch (error) {
    console.error('Error ending conference:', error);
    showToast(`Failed to end conference: ${error.message}`, 'error');
    debugLog('CONFERENCE', `✗ Conference end failed: ${error.message}`);
    return false;
  }
}

/**
 * Check if a session can be merged with another session
 * Returns the other session if available, null otherwise
 * @param {string} currentSessionId - Current session ID
 * @returns {Object|null} - Other mergeable session or null
 */
export function getMergeableSession(currentSessionId) {
  const state = store.getState();
  const allSessions = state.call.sessions || [];

  // Filter out the current session and find established sessions not in a conference
  const mergeableSessions = allSessions.filter(
    (s) =>
      s.id !== currentSessionId &&
      s.state === CALL_STATUS.ESTABLISHED &&
      !isSessionInConference(s) &&
      !s.isBeingTransferred
  );

  return mergeableSessions.length > 0 ? mergeableSessions[0] : null;
}

/**
 * Get all sessions that can be merged into a conference
 * Returns array of established sessions not already in a conference
 * @returns {Array} - Array of mergeable sessions
 */
export function getMergeableSessions() {
  const state = store.getState();
  const allSessions = state.call.sessions || [];

  // Find all established sessions not in a conference
  const mergeableSessions = allSessions.filter(
    (s) => s.state === CALL_STATUS.ESTABLISHED && !isSessionInConference(s) && !s.isBeingTransferred
  );

  return mergeableSessions;
}

/**
 * Add new participants to an existing conference
 * @param {...string} sessionIds - Session IDs to add to conference (can be array or individual arguments)
 * @returns {Promise<boolean>} - Success status
 */
export async function addToConference(...sessionIds) {
  const sessionIdArray = Array.isArray(sessionIds[0]) ? sessionIds[0] : sessionIds;

  console.log('═══════════════════════════════════════════════════');
  console.log(`[ADD TO CONFERENCE] 🎯 Attempting to add ${sessionIdArray.length} sessions`);
  console.log('[ADD TO CONFERENCE] Session IDs to add:', sessionIdArray);

  try {
    // Get active conference
    const conferenceGroupId = getActiveConferenceId();

    if (!conferenceGroupId) {
      showToast('No active conference found', 'error');
      console.error('[ADD TO CONFERENCE] ✗ No active conference');
      return false;
    }

    console.log(`[ADD TO CONFERENCE] Active conference ID: ${conferenceGroupId}`);

    // Get existing conference sessions
    const existingConferenceSessions = getConferenceSessions(conferenceGroupId);
    console.log(
      `[ADD TO CONFERENCE] Existing conference has ${existingConferenceSessions.length} participants`
    );
    existingConferenceSessions.forEach((s, idx) => {
      console.log(`[ADD TO CONFERENCE]   Existing ${idx}: ${s.id} - onHold: ${s.isOnHold}`);
    });

    // Get sessions to add
    const sessionsToAdd = sessionIdArray.map((id) => getSessionById(id)).filter(Boolean);

    if (sessionsToAdd.length === 0) {
      showToast('No valid sessions to add', 'error');
      return false;
    }

    if (sessionsToAdd.length !== sessionIdArray.length) {
      showToast('One or more sessions not found', 'error');
      return false;
    }

    console.log(`[ADD TO CONFERENCE] Found ${sessionsToAdd.length} sessions to add`);
    sessionsToAdd.forEach((s, idx) => {
      console.log(`[ADD TO CONFERENCE]   New ${idx}: ${s.id} - onHold: ${s.isOnHold}`);
    });

    // CRITICAL: Unhold ALL existing conference members
    // When you call C while A+B are in conference, A+B get put on hold
    // We need to unhold them before adding C
    // CRITICAL FIX: Pass skipAutoHold=true to prevent auto-holding other calls
    let unheldExisting = 0;
    // eslint-disable-next-line no-restricted-syntax
    for (const session of existingConferenceSessions) {
      if (session.isOnHold) {
        console.log(
          `[ADD TO CONFERENCE] 🔓 Taking EXISTING conference member ${session.id} off hold`
        );
        // eslint-disable-next-line no-await-in-loop
        await holdSessionById(session.id, false, true); // skipAutoHold=true
        unheldExisting += 1;
      }
    }
    if (unheldExisting > 0) {
      console.log(
        `[ADD TO CONFERENCE] ✓ Unheld ${unheldExisting} existing conference members (without auto-holding)`
      );
    }

    // Take all new sessions off hold if they are on hold
    // CRITICAL FIX: Pass skipAutoHold=true to prevent auto-holding other calls
    let unheldNew = 0;
    // eslint-disable-next-line no-restricted-syntax
    for (const session of sessionsToAdd) {
      if (session.isOnHold) {
        console.log(`[ADD TO CONFERENCE] 🔓 Taking NEW session ${session.id} off hold`);
        // eslint-disable-next-line no-await-in-loop
        await holdSessionById(session.id, false, true); // skipAutoHold=true
        unheldNew += 1;
      }
    }
    if (unheldNew > 0) {
      console.log(`[ADD TO CONFERENCE] ✓ Unheld ${unheldNew} new sessions (without auto-holding)`);
    }

    // CRITICAL FIX: Increased wait time for hold state to fully settle
    // This is crucial when unhiding existing conference members
    console.log('[ADD TO CONFERENCE] ⏳ Waiting for hold state to settle (1500ms)...');
    await new Promise((resolve) => setTimeout(resolve, 1500));

    // Add participants to conference
    console.log(
      `[ADD TO CONFERENCE] 🎵 Adding ${sessionsToAdd.length} participants to conference...`
    );
    const result = await addParticipantsToConference(conferenceGroupId, sessionsToAdd);

    if (!result.success) {
      console.error('[ADD TO CONFERENCE] ✗ Failed to add participants:', result.error);
      throw new Error(result.error || 'Failed to add participants to conference');
    }

    console.log(`[ADD TO CONFERENCE] ✓ Participants added, total now: ${result.participantCount}`);

    // CRITICAL FIX: Post-add verification - ensure all audio elements are playing
    console.log('[ADD TO CONFERENCE] 🔊 Verifying audio elements are playing...');
    const allParticipants = [...existingConferenceSessions, ...sessionsToAdd];
    // eslint-disable-next-line no-restricted-syntax
    for (const session of allParticipants) {
      const audioElement = document.getElementById(`call-audio-${session.id}`);
      if (audioElement) {
        if (audioElement.paused) {
          console.log(
            `[ADD TO CONFERENCE] ⚠️  Audio element paused for session ${session.id}, resuming...`
          );
          try {
            // eslint-disable-next-line no-await-in-loop
            await audioElement.play();
            console.log(`[ADD TO CONFERENCE] ✓ Audio resumed for session ${session.id}`);
          } catch (playError) {
            console.warn(
              `[ADD TO CONFERENCE] Audio play error for session ${session.id}:`,
              playError
            );
          }
        } else {
          console.log(`[ADD TO CONFERENCE] ✓ Audio already playing for session ${session.id}`);
        }
      }
    }

    // Verify Redux state for all participants (existing + new)
    const allSessionIds = [...existingConferenceSessions.map((s) => s.id), ...sessionIdArray];
    const updatedSessions = store.getState().call.sessions;
    console.log('[ADD TO CONFERENCE] 📊 Verifying Redux state after adding:');
    allSessionIds.forEach((sessionId) => {
      const session = updatedSessions.find((s) => s.id === sessionId);
      if (session) {
        console.log(`[ADD TO CONFERENCE]   Session ${sessionId}:`, {
          isConference: session.isConference,
          conferenceGroupId: session.conferenceGroupId,
          isOnHold: session.isOnHold,
        });
      } else {
        console.error(`[ADD TO CONFERENCE]   ✗ Session ${sessionId} not found!`);
      }
    });

    logConferenceEvent('PARTICIPANTS_ADDED', {
      conferenceId: conferenceGroupId,
      addedSessions: sessionIdArray,
      totalParticipants: result.participantCount,
    });

    showToast(`Added ${sessionsToAdd.length} participant(s) to conference`, 'success');
    console.log('[ADD TO CONFERENCE] ✓✓✓ Successfully added participants to conference');
    console.log('═══════════════════════════════════════════════════');

    return true;
  } catch (error) {
    console.error('═══════════════════════════════════════════════════');
    console.error('[ADD TO CONFERENCE] ✗✗✗ Error adding to conference:', error);
    console.error('[ADD TO CONFERENCE] Error stack:', error.stack);
    showToast(`Failed to add to conference: ${error.message}`, 'error');
    console.error('═══════════════════════════════════════════════════');
    return false;
  }
}

/**
 * Handle session termination for conference calls
 * If one session in a conference ends, end the conference and restore the other
 * @param {string} sessionId - Session ID that is terminating
 */
export async function handleConferenceSessionEnd(sessionId) {
  const session = getSessionById(sessionId);

  // Check if session exists and is in a conference
  if (!session || !isSessionInConference(session)) {
    return; // Not in a conference, normal termination
  }

  const { conferenceGroupId } = session;

  if (!conferenceGroupId) {
    debugLog('CONFERENCE', `Session ${sessionId} marked as conference but has no group ID`);
    return;
  }

  debugLog('CONFERENCE', `Conference session ending: ${sessionId}, group: ${conferenceGroupId}`);

  const conferenceSessions = getConferenceSessions(conferenceGroupId);

  // Find the other session(s) in the conference
  const otherSessions = conferenceSessions.filter((s) => s.id !== sessionId);

  debugLog('CONFERENCE', `Found ${otherSessions.length} other session(s) in conference`);

  if (otherSessions.length > 0) {
    debugLog('CONFERENCE', 'Restoring other session(s) after conference member left');

    // eslint-disable-next-line no-restricted-syntax
    for (const otherSession of otherSessions) {
      try {
        // Restore original audio if available
        if (otherSession.rawSession?.originalAudioTrack) {
          // eslint-disable-next-line no-await-in-loop
          await restoreOriginalAudio(otherSession, otherSession.rawSession.originalAudioTrack);
          delete otherSession.rawSession.originalAudioTrack;
          delete otherSession.rawSession.conferenceGroupId;
          debugLog('CONFERENCE', `Restored audio for session: ${otherSession.id}`);
        }

        // IMPORTANT: Update Redux state to clear conference flags
        store.dispatch(
          updateSession({
            id: otherSession.id,
            isConference: false,
            conferenceGroupId: null,
          })
        );

        debugLog('CONFERENCE', `Cleared conference flags for session: ${otherSession.id}`);
      } catch (error) {
        console.error(`Error restoring session ${otherSession.id}:`, error);
        // Continue with other sessions even if one fails
      }
    }
  }

  // End the conference in Redux
  try {
    store.dispatch(endConference(conferenceGroupId));
    debugLog('CONFERENCE', `Ended conference group: ${conferenceGroupId}`);
  } catch (error) {
    console.error('Error ending conference in Redux:', error);
  }

  // Also clear conference flags from the ending session (if it still exists in Redux)
  try {
    const stillExists = getSessionById(sessionId);
    if (stillExists) {
      store.dispatch(
        updateSession({
          id: sessionId,
          isConference: false,
          conferenceGroupId: null,
        })
      );
      debugLog('CONFERENCE', `Cleared conference flags for ending session: ${sessionId}`);
    }
  } catch (error) {
    console.error('Error clearing ending session flags:', error);
  }

  logConferenceEvent('CONFERENCE_SESSION_LEFT', { sessionId, conferenceId: conferenceGroupId });
  showToast('Conference ended - participant disconnected', 'info');
}
